import React, { useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';

const useStyles = makeStyles(theme => ({
    jsonViewer: {
        fontFamily: 'monospace',
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.shape.borderRadius,
        overflow: 'auto',
        padding: theme.spacing(4),
        margin: '0 32px 24px',
        maxHeight: '60vh',
        overflowWrap: 'anywhere'
    },
    jsonKey: {
        color: '#a31515',
        fontWeight: 'bold'
    },
    jsonString: {
        color: '#0b7500',
        wordBreak: 'break-word'
    },
    jsonNumber: {
        color: '#1a01cc',
        wordBreak: 'break-word'
    },
    jsonBoolean: {
        color: '#0000ff',
        wordBreak: 'break-word'
    },
    jsonNull: {
        color: '#950084',
        wordBreak: 'break-word'
    },
    jsonLine: {
        display: 'block'
    },
    innerKeyDiv: {
        flex: '1 1 auto',
        fontWeight: 500,
        overflowWrap: 'anywhere',
        minWidth: 0
    },
    payloadHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        margin: '8px 32px',
        flexWrap: 'wrap',
        rowGap: theme.spacing(1)
    },
    copyButton: {
        color: '#777'
    },
    jsonContent: {
        minWidth: 'fit-content'
    }
}));

export default function JsonViewer(props) {
    const { src } = props;
    const classes = useStyles();
    const safeSource = useMemo(() => (src ? src : {}), [src]);
    const [clipboardInfo, setClipboardInfo] = useState({
        open: false,
        msg: 'Copy to Clipboard'
    });

    const handleTooltipOpen = () => {
        setClipboardInfo({ open: true, msg: 'Copy to Clipboard' });
    };

    const handleTooltipClose = () => {
        setClipboardInfo(prev => ({ ...prev, open: false }));
    };

    const openTooltip = msg => {
        setClipboardInfo({ open: true, msg });
    };

    const copyToClipboard = async jsonObj => {
        try {
            // Convert the JSON object to a properly formatted string
            const jsonString = JSON.stringify(jsonObj, null, 2);
            // Copy the string to clipboard
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(jsonString);
                openTooltip('Copied to Clipboard');
                return;
            }
            // Clipboard API unavailable, proceed to fallback method
        } catch (error) {
            try {
                const jsonString = JSON.stringify(jsonObj, null, 2);
                const textArea = document.createElement('textarea');
                textArea.value = jsonString;
                textArea.setAttribute('readonly', '');
                textArea.style.position = 'absolute';
                textArea.style.left = '-9999px';
                document.body.appendChild(textArea);
                const selection = document.getSelection();
                const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
                textArea.select();
                const copySuccess = document.execCommand('copy');
                document.body.removeChild(textArea);
                if (range && selection) {
                    selection.removeAllRanges();
                    selection.addRange(range);
                }
                if (copySuccess) {
                    openTooltip('Copied to Clipboard');
                } else {
                    openTooltip('Failed to copy');
                }
            } catch (fallbackError) {
                console.error('Failed to copy:', fallbackError);
                openTooltip('Failed to copy');
            }
        }
    };
    
    const renderJson = (obj, level = 0) => {
        if (obj === null) return <span className={classes.jsonNull}>null</span>;
        if (typeof obj === 'boolean') return <span className={classes.jsonBoolean}>{String(obj)}</span>;
        if (typeof obj === 'number') return <span className={classes.jsonNumber}>{obj}</span>;
        if (typeof obj === 'string') return <span className={classes.jsonString}>"{obj}"</span>;

        if (Array.isArray(obj)) {
            if (obj.length === 0) return '[]';

            return (
                <>
                    <span>[</span>
                    {obj.map((item, index) => (
                        <div key={index} className={classes.jsonLine} style={{ marginLeft: (level + 1) * 20 }}>
                            {renderJson(item, level + 1)}
                            {index < obj.length - 1 ? ',' : ''}
                        </div>
                    ))}
                    <div className={classes.jsonLine} style={{ marginLeft: level * 20 }}>]</div>
                </>
            );
        }

        if (typeof obj === 'object') {
            const keys = Object.keys(obj);
            if (keys.length === 0) return '{}';

            return (
                <>
                    <span>{'{'}</span>
                    {keys.map((key, index) => (
                        <div key={key} className={classes.jsonLine} style={{ marginLeft: (level + 1) * 20 }}>
                            <span className={classes.jsonKey}>"{key}"</span>: {renderJson(obj[key], level + 1)}
                            {index < keys.length - 1 ? ',' : ''}
                        </div>
                    ))}
                    <div className={classes.jsonLine} style={{ marginLeft: level * 20 }}>{'}'}</div>
                </>
            );
        }

        return String(obj);
    };

    return (
        <div>
            <div className={classes.payloadHeader}>
                <div className={classes.innerKeyDiv}>Event Payload :</div>
                <Tooltip
                    open={clipboardInfo.open}
                    onClose={handleTooltipClose}
                    title={clipboardInfo.msg}
                    disableFocusListener
                    disableTouchListener
                >
                    <span>
                        <IconButton
                            className={classes.copyButton}
                            onMouseEnter={handleTooltipOpen}
                            onMouseLeave={handleTooltipClose}
                            onClick={() => copyToClipboard(src)}
                        >
                            <FileCopyOutlinedIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </div>
            <div className={classes.jsonViewer}>
                <div className={classes.jsonContent}>{renderJson(safeSource)}</div>
            </div>
        </div>
    );
}
